import { Controller, useForm } from 'react-hook-form'
import { BpSelect } from '../../../shared/components/forms/select/BpSelect'
import { ILiveUncloakingFilter } from '../model/types/LiveUncloakingFilter.interface'
import { useAppDispatch, useAppSelector } from '../../../app/store/hooks/Redux'
import { BpSearchInput } from '../../../shared/components/forms/search-input/BpSearchInput'
import { DeviceIcon } from '../../../shared/components/icons/filters/DeviceIcon'
import { BpButton } from '../../../shared/components/buttons/bp-button/BpButton'
import { IconRoundedCheck } from '../../../shared/components/icons/IconRoundedCheck'
import { IWithSearchParamsProps, withSearchParams } from '../../../shared/lib/hoc/WithSearchParams'
import {
  selectLiveUncloakingFilters,
  setLiveUncloakingApplyFilters,
  setLiveUncloakingExcludeIds,
  setLiveUncloakingExcludeProxy,
  setLiveUncloakingFilter,
} from '../model/liveUncloakingSlice'
import { BpTooltip } from '../../../shared/components/cards/tooltip/BpTooltip'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetDecloakCountryQuery, useGetFilterDevicesQuery } from '../../../app/api'
import clsx from 'clsx'
import styles from './LiveUncloakingFilters.module.scss'
import { CountryIcon } from '../../../shared/components/icons/filters/CountryIcon'
import { DomainIcon } from '../../../shared/components/icons/filters/DomainIcon'

const Filter = ({
  searchParams,
  setSearchParams,
}: IWithSearchParamsProps<ILiveUncloakingFilter>) => {
  const { t } = useTranslation()
  const { device, country, site } = useAppSelector<ILiveUncloakingFilter>(
    selectLiveUncloakingFilters,
  )
  const dispatch = useAppDispatch()

  const {
    getValues,
    handleSubmit,
    control,
    formState: { defaultValues },
  } = useForm<ILiveUncloakingFilter>({
    mode: 'onChange',
    defaultValues: { device, country, site, ...searchParams },
  })

  const applyFilter = () => {
    const values = getValues()
    dispatch(setLiveUncloakingApplyFilters(true))
    dispatch(setLiveUncloakingExcludeProxy(false))
    dispatch(setLiveUncloakingFilter(values))
    if (setSearchParams) setSearchParams(values)
  }

  const {
    data: countryList,
    isFetching: countriesFetching,
    isLoading: countriesLoading,
  } = useGetDecloakCountryQuery()
  const countries = useMemo(
    () => countryList?.data?.map((o) => ({ text: o.name ?? '', value: `${o.id}` })),
    [countryList],
  )

  const {
    data: devices,
    isFetching: devicesFetching,
    isLoading: devicesLoading,
  } = useGetFilterDevicesQuery()

  return (
    <>
      <form
        className={clsx(styles.form)}
        onChange={() => {
          dispatch(setLiveUncloakingExcludeIds(undefined))
        }}
        onSubmit={handleSubmit(applyFilter)}
      >
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field }) => {
              const defaultValue = defaultValues?.country
                ? defaultValues.country
                : country
                  ? country
                  : countries && countries[0].value

              return (
                <BpSelect
                  {...field}
                  Icon={CountryIcon}
                  defaultValue={defaultValue}
                  disabled={countriesLoading || countriesFetching}
                  fieldSize='lg'
                  loading={countriesLoading || countriesFetching}
                  options={countries}
                  title={t('fields.country')}
                ></BpSelect>
              )
            }}
            control={control}
            name='country'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field }) => {
              const defaultValue = defaultValues?.device
                ? defaultValues.device
                : device
                  ? device
                  : devices && 72

              return (
                <BpSelect
                  {...field}
                  Icon={DeviceIcon}
                  defaultValue={defaultValue}
                  disabled={devicesLoading || devicesFetching}
                  fieldSize='lg'
                  loading={devicesLoading || devicesFetching}
                  options={devices}
                  title={t('fields.device')}
                ></BpSelect>
              )
            }}
            control={control}
            name='device'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field }) => {
              return (
                <BpSearchInput
                  {...field}
                  Icon={DomainIcon}
                  defaultValue={defaultValues?.site}
                  fieldSize='lg'
                  placeholder={t('fields.enterLinkToUncloak')}
                />
              )
            }}
            control={control}
            name='site'
          />
        </div>
        <div className={clsx(styles.buttonWrapper)}>
          <BpButton iconLeft={<IconRoundedCheck />} size='lg'>
            {t('buttons.apply')}
          </BpButton>
        </div>
        <div className={clsx(styles.proxyButtonWrapper)}>
          <button
            className={clsx(styles.proxyButton)}
            type='button'
            onClick={() => {
              const values = getValues()
              dispatch(setLiveUncloakingApplyFilters(true))
              dispatch(setLiveUncloakingExcludeProxy(true))
              dispatch(setLiveUncloakingFilter(values))
              if (setSearchParams) setSearchParams(values)
            }}
          >
            {t('buttons.tryWithNewProxy')}
          </button>
          <BpTooltip
            arrowPointAtCenter
            placement='topRight'
            title={t('pages.liveUncloaking.tryWithNewProxyTooltip')}
          />
        </div>
      </form>
    </>
  )
}

export const LiveUncloakingFilters = withSearchParams<
  IWithSearchParamsProps<ILiveUncloakingFilter>,
  ILiveUncloakingFilter
>(Filter, selectLiveUncloakingFilters)
